// components/UrwPaymentNotice.tsx
//
// What every URW account sees when it opens the app: access is limited because of
// unpaid invoices. The account can still navigate and download its own work; the
// agent and every model are refused server-side (lib/access-suspension.ts, enforced
// in lib/actions.ts and app/api/ai/assist/route.ts). Appears from Monday 2026-08-17
// (Paris time), once per browser session, until the restriction is lifted by removing
// this component's render in app/(app)/layout.tsx and clearing the id from the shared
// module. The wording and the arming date live THERE, not here: the notice and the
// refusals must never be able to disagree.

'use client';

import React, { useState } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { clientAccessSuspension, SUSPENSION_MESSAGE } from '#lib/access-suspension';

/** Once per browser SESSION: the notice must open every session, but showing
 *  it again on every navigation inside the same session is noise. */
const SEEN_KEY = 'fater.urw.paymentNoticeSeen';

const hasSeenNotice = (): boolean => {
  try {
    return sessionStorage.getItem(SEEN_KEY) === '1';
  } catch {
    // Private mode / storage disabled: show it.
    return false;
  }
};

const UrwPaymentNotice = () => {
  // The organisation loads asynchronously after mount; the selector re-renders
  // this component when it lands, so the gate below needs no effect.
  const organisationId = useKernelStore((state) => state.userContext.organisationId);
  const teamId = useKernelStore((state) => state.userContext.teamId);
  const serverSuspension = useKernelStore((state) => state.userContext.accessSuspension);
  const [dismissed, setDismissed] = useState(false);

  // Clock read lives in a lazy initializer, once per mount: render stays pure
  // (react-hooks/purity forbids Date.now() in render). A tab opened before
  // Monday and kept open stays unarmed until its next session; accepted.
  // Only the CLOCK is captured once: react-hooks/purity forbids Date.now() during render,
  // and the arming instant cannot change under us anyway. The ORGANISATION must be compared
  // during render, because it loads asynchronously after mount: capturing it in a lazy
  // initialiser froze the pre-load value (null) and the notice never appeared, while the
  // server-side block still refused the account. Silently blocked is the worst outcome.
  const [now] = useState(() => Date.now());
  // Server verdict first, this bundle's tables only until it arrives: the note about a tab opened
  // before Monday staying unarmed applies to the fallback alone now.
  const suspended = clientAccessSuspension(serverSuspension, organisationId, teamId, now).suspended;

  if (dismissed || !suspended) return null;
  if (hasSeenNotice()) return null;

  const close = () => {
    try {
      sessionStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* nothing to do: it will show again next time */
    }
    setDismissed(true);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-xl border border-[#2A2A2A] bg-[#1B1B1B] p-6 shadow-xl">
        <p className="mb-4 text-[11px] uppercase tracking-wide text-[#8A8A8A]">Account notice</p>

        <div className="space-y-4 text-[13px] leading-relaxed text-[#D0D0D0]">
          <p>{SUSPENSION_MESSAGE}</p>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={close}
            className="h-8 rounded-full bg-[#2E2E2E] px-4 text-[12px] text-[#D0D0D0] transition-colors hover:bg-[#3A3A3A]"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default UrwPaymentNotice;
